/**
 * The language this product writes dates in.
 *
 * `toLocaleDateString()` with no locale follows the READER's machine, which is
 * not the same thing as the application's language. On a Spanish-configured
 * browser the portal rendered "9 sept 2026" inside English copy, and a bare
 * `toLocaleDateString()` rendered "9/8/2026" — a date that means the 9th of
 * August in Toronto and the 8th of September in London.
 *
 * `components/ui/Money` already pins en-CA for currency, for exactly this
 * reason: two people reading the same screen must not see the same fact written
 * two different ways. Dates follow the same rule.
 *
 * Two shapes, chosen by what the date sits next to:
 *
 *   SPELLED  "Sep 8, 2026"  — prose, cards, anything a person reads once.
 *   NUMERIC  "2026-09-08"   — invoices and tables, where columns are narrow and
 *                             an ISO-ordered date is both compact and impossible
 *                             to misread in any country.
 */

export const APP_LOCALE = "en-CA";

export const DATE_SPELLED = Object.freeze({dateStyle: "medium"});
export const DATE_NUMERIC = Object.freeze({
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

function toDate(value) {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "Sep 8, 2026", or "" when there is no usable date — never "Invalid Date". */
export function formatDateSpelled(value) {
  const date = toDate(value);
  return date ? date.toLocaleDateString(APP_LOCALE, DATE_SPELLED) : "";
}

/** "2026-09-08", for table cells and printed documents. */
export function formatDateNumeric(value) {
  const date = toDate(value);
  return date ? date.toLocaleDateString(APP_LOCALE, DATE_NUMERIC) : "";
}
